/*
 * Admin → Devices console.
 *
 * Lists every device bound to a supervisor account, grouped by status.
 *   • Pending tab → shows new-device approval requests with the
 *     supervisor's attached unlock selfie so the admin can visually
 *     verify identity before clicking "Approve".
 *   • Approved tab → shows currently-trusted devices with a "Revoke"
 *     action; revoking immediately blocks that device from capturing
 *     attendance until re-approval.
 *   • Revoked tab → historical revoked devices with a "Re-approve"
 *     option in case the revoke was a mistake.
 */
import React, { useEffect, useMemo, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShieldCheck, ShieldAlert, RefreshCw, Smartphone, Clock, Eye, Trash2 } from "lucide-react";
import { toast } from "sonner";

function tsFmt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    // Always render in India Standard Time. We deliberately don't rely
    // on the browser's local timezone here because admins log in from
    // varied environments and the backend stores all timestamps in UTC.
    return d.toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return iso;
  }
}

function uaShort(ua) {
  if (!ua) return "Unknown device";
  // Crude: pick OS + browser hints. Good enough for admin display.
  const m = ua.match(/\(([^)]+)\)/);
  const env = m ? m[1].split(";")[0].trim() : "";
  let browser = "Browser";
  if (/Chrome\/\d/.test(ua) && !/Edg\//.test(ua)) browser = "Chrome";
  else if (/Firefox\/\d/.test(ua)) browser = "Firefox";
  else if (/Safari\/\d/.test(ua) && !/Chrome\//.test(ua)) browser = "Safari";
  else if (/Edg\//.test(ua)) browser = "Edge";
  return env ? `${browser} · ${env}` : browser;
}

export default function AdminDevicesPage() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  // Split between ITCOT (PMC) supervisors and Contractor supervisors.
  // The two cohorts are managed and onboarded independently so we group
  // their device approvals separately in the admin console.
  const [roleSegment, setRoleSegment] = useState("pmc_supervisor");
  const [selfieView, setSelfieView] = useState(null);
  const [pendingCounts, setPendingCounts] = useState({ pmc_count: 0, contractor_count: 0, incharge_count: 0, total: 0 });

  async function refresh() {
    setLoading(true);
    try {
      const [devRes, countRes] = await Promise.all([
        api.get("/admin/devices", { params: { role: roleSegment } }),
        api.get("/admin/devices/pending-count"),
      ]);
      setDevices(devRes.data || []);
      if (countRes.data) {
        setPendingCounts({
          pmc_count: countRes.data.pmc_count || 0,
          contractor_count: countRes.data.contractor_count || 0,
          incharge_count: countRes.data.incharge_count || 0,
          total: countRes.data.total || 0,
        });
      }
    } catch (e) {
      toast.error("Failed to load devices");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { refresh(); }, [roleSegment]); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = useMemo(() => {
    const out = { pending: [], approved: [], revoked: [] };
    devices.forEach((d) => {
      (out[d.status] || (out[d.status] = [])).push(d);
    });
    return out;
  }, [devices]);

  async function approve(d) {
    try {
      await api.post(`/admin/devices/${d.id}/approve`);
      toast.success(`Approved device for ${d.user_name || d.username}`);
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object")
        ? (detail.message || JSON.stringify(detail))
        : (detail || "Failed to approve");
      toast.error(msg);
    }
  }
  async function revoke(d) {
    if (!window.confirm(`Revoke device for ${d.user_name || d.username}? They will be blocked from capturing attendance until re-approved.`)) return;
    try {
      await api.post(`/admin/devices/${d.id}/revoke`);
      toast.success(`Revoked device for ${d.user_name || d.username}`);
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke");
    }
  }
  async function hardDelete(d) {
    if (!window.confirm(`Permanently delete this device row? The next login from that physical device will register a fresh row (auto-approved if it's the user's only one).`)) return;
    try {
      await api.delete(`/admin/devices/${d.id}`);
      toast.success("Deleted");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete");
    }
  }

  return (
    <AppShell
      title="Device Approvals"
      subtitle="Approve, revoke, and review supervisor devices used for attendance capture."
      action={
        <Button size="sm" variant="outline" onClick={refresh} disabled={loading} data-testid="devices-refresh-btn">
          <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      }
    >
      <Card className="p-4">
        {/* Role segmented control — ITCOT supervisors vs Contractor supervisors vs Supervisor In-Charge */}
        <div className="mb-4 inline-flex flex-wrap rounded-md border bg-slate-50 p-0.5" data-testid="device-role-segment">
          <button
            type="button"
            onClick={() => setRoleSegment("pmc_supervisor")}
            className={`flex items-center gap-1.5 rounded-[5px] px-3 py-1 text-sm transition ${
              roleSegment === "pmc_supervisor"
                ? "bg-white font-semibold text-slate-900 shadow-sm ring-1 ring-slate-200"
                : "text-slate-600 hover:text-slate-900"
            }`}
            data-testid="device-role-pmc"
          >
            ITCOT Supervisor
            {pendingCounts.pmc_count > 0 && (
              <Badge className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-1.5 py-0.2 text-[10px] rounded-full">
                {pendingCounts.pmc_count}
              </Badge>
            )}
          </button>
          <button
            type="button"
            onClick={() => setRoleSegment("contractor_supervisor")}
            className={`flex items-center gap-1.5 rounded-[5px] px-3 py-1 text-sm transition ${
              roleSegment === "contractor_supervisor"
                ? "bg-white font-semibold text-slate-900 shadow-sm ring-1 ring-slate-200"
                : "text-slate-600 hover:text-slate-900"
            }`}
            data-testid="device-role-contractor"
          >
            Contractor Supervisor
            {pendingCounts.contractor_count > 0 && (
              <Badge className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-1.5 py-0.2 text-[10px] rounded-full">
                {pendingCounts.contractor_count}
              </Badge>
            )}
          </button>
          <button
            type="button"
            onClick={() => setRoleSegment("supervisor_incharge")}
            className={`flex items-center gap-1.5 rounded-[5px] px-3 py-1 text-sm transition ${
              roleSegment === "supervisor_incharge"
                ? "bg-white font-semibold text-slate-900 shadow-sm ring-1 ring-slate-200"
                : "text-slate-600 hover:text-slate-900"
            }`}
            data-testid="device-role-incharge"
          >
            Supervisor In-Charge
            {pendingCounts.incharge_count > 0 && (
              <Badge className="bg-amber-500 hover:bg-amber-600 text-white font-bold px-1.5 py-0.2 text-[10px] rounded-full">
                {pendingCounts.incharge_count}
              </Badge>
            )}
          </button>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="mb-4">
            <TabsTrigger value="pending" data-testid="devices-tab-pending">
              Pending ({grouped.pending.length})
            </TabsTrigger>
            <TabsTrigger value="approved" data-testid="devices-tab-approved">
              Approved ({grouped.approved.length})
            </TabsTrigger>
            <TabsTrigger value="revoked" data-testid="devices-tab-revoked">
              Revoked ({grouped.revoked.length})
            </TabsTrigger>
          </TabsList>

          {/* Pending — show selfie thumbnail for visual identity check */}
          <TabsContent value="pending">
            {grouped.pending.length === 0 ? (
              <EmptyState text="No pending device approval requests." />
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {grouped.pending.map((d) => (
                  <DeviceCard
                    key={d.id}
                    d={d}
                    onApprove={() => approve(d)}
                    onDelete={() => hardDelete(d)}
                    onViewSelfie={() => setSelfieView(d)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="approved">
            {grouped.approved.length === 0 ? (
              <EmptyState text="No approved devices yet." />
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {grouped.approved.map((d) => (
                  <DeviceCard
                    key={d.id}
                    d={d}
                    onRevoke={() => revoke(d)}
                    onDelete={() => hardDelete(d)}
                    onViewSelfie={() => setSelfieView(d)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="revoked">
            {grouped.revoked.length === 0 ? (
              <EmptyState text="No revoked devices." />
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {grouped.revoked.map((d) => (
                  <DeviceCard
                    key={d.id}
                    d={d}
                    onApprove={() => approve(d)}
                    onDelete={() => hardDelete(d)}
                    onViewSelfie={() => setSelfieView(d)}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </Card>

      {/* Full-size selfie preview */}
      {selfieView ? (
        <Dialog open onOpenChange={() => setSelfieView(null)}>
          <DialogContent className="max-w-2xl">
            <DialogHeader className="pr-8">
              <DialogTitle className="pr-2">
                Unlock selfie · {selfieView.user_name} ({selfieView.username})
              </DialogTitle>
            </DialogHeader>
            <div className="flex max-h-[70vh] items-center justify-center overflow-auto bg-slate-900/95">
              {selfieView.unlock_selfie_b64 ? (
                <img
                  src={selfieView.unlock_selfie_b64.startsWith("data:") ? selfieView.unlock_selfie_b64 : `data:image/jpeg;base64,${selfieView.unlock_selfie_b64}`}
                  alt="unlock selfie"
                  className="max-h-[70vh] object-contain"
                />
              ) : (
                <div className="p-12 text-sm text-slate-300">No selfie attached.</div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </AppShell>
  );
}

function EmptyState({ text }) {
  return (
    <div className="rounded-md border border-dashed p-10 text-center text-sm text-muted-foreground">
      {text}
    </div>
  );
}

function DeviceCard({ d, onApprove, onRevoke, onDelete, onViewSelfie }) {
  const isApproved = d.status === "approved";
  const isPending = d.status === "pending";
  const isRevoked = d.status === "revoked";
  const tone = isApproved
    ? "bg-emerald-50 ring-emerald-200 text-emerald-900"
    : isPending
      ? "bg-amber-50 ring-amber-200 text-amber-900"
      : "bg-rose-50 ring-rose-200 text-rose-900";
  return (
    <Card className="p-3" data-testid={`device-card-${d.id}`}>
      <div className="flex items-start gap-3">
        {/* Selfie thumbnail (any status — approved/revoked rows keep the
            identity-check photo on file too) or generic device icon when
            none was ever submitted. */}
        {d.unlock_selfie_b64 ? (
          <button
            type="button"
            onClick={onViewSelfie}
            className="relative h-20 w-20 flex-none overflow-hidden rounded ring-1 ring-slate-200 hover:ring-sky-400"
            title="View full-size selfie"
            data-testid={`device-selfie-thumb-${d.id}`}
          >
            <img
              src={d.unlock_selfie_b64.startsWith("data:") ? d.unlock_selfie_b64 : `data:image/jpeg;base64,${d.unlock_selfie_b64}`}
              alt="selfie"
              className="h-full w-full object-cover"
            />
            <span className="absolute bottom-0 right-0 m-0.5 grid h-5 w-5 place-items-center rounded-full bg-slate-900/85 text-white">
              <Eye className="h-3 w-3" />
            </span>
          </button>
        ) : (
          <div className="grid h-20 w-20 flex-none place-items-center rounded bg-slate-100 text-slate-400 ring-1 ring-slate-200">
            <Smartphone className="h-7 w-7" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <div className="text-sm font-semibold text-slate-900" data-testid={`device-user-${d.id}`}>
              {d.user_name || d.username}
            </div>
            <div className="text-[11px] font-mono text-muted-foreground">@{d.username}</div>
            <Badge variant="outline" className={`ml-auto text-[10px] ring-1 ${tone}`} data-testid={`device-status-${d.id}`}>
              {isApproved
                ? d.auto_approved ? "Approved (auto, first device)" : "Approved"
                : isPending
                  ? "Pending"
                  : "Revoked"}
            </Badge>
          </div>
          <div className="mt-1 text-[12px] text-slate-700">{uaShort(d.user_agent)}</div>
          <div className="mt-0.5 grid grid-cols-1 gap-x-3 text-[11px] text-muted-foreground sm:grid-cols-2">
            {d.requested_at ? <div><Clock className="mr-1 inline h-3 w-3" />Requested: {tsFmt(d.requested_at)}</div> : null}
            {d.approved_at ? <div><ShieldCheck className="mr-1 inline h-3 w-3" />Approved: {tsFmt(d.approved_at)}</div> : null}
            {d.revoked_at ? <div><ShieldAlert className="mr-1 inline h-3 w-3" />Revoked: {tsFmt(d.revoked_at)}</div> : null}
            {d.last_seen_at ? <div>Last seen: {tsFmt(d.last_seen_at)}</div> : null}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {isPending ? (() => {
              const hasSelfie = !!d.unlock_selfie_b64;
              return (
                <>
                  <Button
                    size="sm"
                    onClick={onApprove}
                    disabled={!hasSelfie}
                    title={hasSelfie ? "Approve device" : "Waiting for supervisor to submit unlock selfie"}
                    data-testid={`device-approve-${d.id}`}
                  >
                    <ShieldCheck className="mr-1.5 h-4 w-4" /> Approve
                  </Button>
                  {!hasSelfie ? (
                    <span
                      className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-900"
                      data-testid={`device-selfie-waiting-${d.id}`}
                    >
                      Waiting for selfie
                    </span>
                  ) : null}
                </>
              );
            })() : null}
            {isApproved ? (
              <Button size="sm" variant="destructive" onClick={onRevoke} data-testid={`device-revoke-${d.id}`}>
                <ShieldAlert className="mr-1.5 h-4 w-4" /> Revoke
              </Button>
            ) : null}
            {isRevoked ? (
              <Button size="sm" onClick={onApprove} data-testid={`device-reapprove-${d.id}`}>
                <ShieldCheck className="mr-1.5 h-4 w-4" /> Re-approve
              </Button>
            ) : null}
            <Button size="sm" variant="ghost" onClick={onDelete} data-testid={`device-delete-${d.id}`}>
              <Trash2 className="mr-1.5 h-4 w-4" /> Delete row
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}
